import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EditEngineer from "./EditEngineer";
import "./ViewEngineer.css";
import apiService from "../../utils/apiservice";

// ✅ TOASTIFY
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";




const API_URL = process.env.REACT_APP_API_DJANGO_URL;

const ViewSiteEngineer = () => {
    const [rows, setRows] = useState([]);
    const [selectedEngineer, setSelectedEngineer] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [engineerToDelete, setEngineerToDelete] = useState(null);

    const [deleteMessage, setDeleteMessage] = useState("");
    const [deleteError, setDeleteError] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Reset page when the data changes (e.g., after a delete or fetch)
    useEffect(() => {
        setPage(0);
    }, [rows.length]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // ✅ Fetch Engineers from API
    useEffect(() => {
        fetchEngineers();
    }, []);

    const fetchEngineers = async () => {
        try {
            setLoading(true);

            const token = localStorage.getItem("accessToken");
            const supervisorId = localStorage.getItem("userId"); // supervisor's user_id stored

            console.log("Supervisor ID:", supervisorId); // debug

            if (!supervisorId) {
                toast.error("Supervisor ID not found");
                return;
            }

            const customerId = localStorage.getItem("customer_id");

            if (!customerId) {
                toast.error("Customer ID not found");
                return;
            }

            const response = await apiService.get(
                `/api/get_customer_engineers/${customerId}/`,
                {
                    data: {}
                }
            );
            // Format data for table
            const formattedEngineers = response.data.engineers.map((eng) => ({
                id: eng.id,
                first_name: eng.first_name,
                last_name: eng.last_name,
                email: eng.email,
                phone_no: eng.phone_no,
                supervisor_name: response.data.supervisor_name,
                // site_name:
                //     eng.assigned_sites && eng.assigned_sites.length > 0
                //         ? eng.assigned_sites.join(", ")
                //         : "No Sites Assigned",
                site_name:
                    eng.assigned_sites && eng.assigned_sites.length > 0
                        ? eng.assigned_sites.map(site => site.site_name).join(", ")
                        : "No Sites Assigned",
            }));

            setRows(formattedEngineers);
        } catch (error) {
            console.error("Error fetching engineers:", error);

            if (error.response?.status === 401) {
                toast.error("Unauthorized! Please login again.");
            } else {
                toast.error(error.response?.data?.error || "Failed to fetch engineers");
            }
        } finally {
            setLoading(false);
        }
    };

    // --- Edit Logic ---
    const handleEditClick = (engineer) => {
        setSelectedEngineer(engineer);
        setEditOpen(true);
    };
    const handleSaveEdit = async (updatedEngineer) => {
        try {
            const token = localStorage.getItem("accessToken");

            if (!token) {
                toast.error("Session expired. Please login again.");
                return;
            }

            const response = await axios({
                url: `${API_URL}/api/update_engineer/${updatedEngineer.id}/`,
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {
                    first_name: updatedEngineer.first_name,
                    last_name: updatedEngineer.last_name,
                    phone_no: updatedEngineer.phone_no,
                    email: updatedEngineer.email,
                },
            });

            console.log("Update Response:", response.data);

            // ✅ Refresh list after update
            await fetchEngineers();

            setEditOpen(false);
            setSelectedEngineer(null);

            toast.success("Engineer updated successfully!");
        } catch (error) {
            console.error("Update Error:", error);

            if (error.response?.status === 401) {
                toast.error("Unauthorized! Please login again.");
            } else {
                toast.error(error.response?.data?.error || "Update failed");
            }
        }
    };
    // --- Delete Logic ---
    const handleDeleteClick = (engineer) => {
        setEngineerToDelete(engineer);
        setDeleteOpen(true);
        setDeleteMessage("");
        setDeleteError(false);
    };

    const handleConfirmDelete = async () => {
        if (!engineerToDelete) return;

        try {
            const token = localStorage.getItem("accessToken");

            const response = await axios({
                url: `${API_URL}/api/delete_engineer/${engineerToDelete.id}/`,
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                data: {},
            });

            // ✅ Use backend message
            const msg = response.data?.message || "Engineer deleted successfully";

            toast.success("Site Engineer Deleted Successfully"); // ✅ clean toast

            // ✅ Refresh table
            await fetchEngineers();

            // ✅ Close dialog
            setDeleteOpen(false);
        } catch (error) {
            console.error("Delete Error:", error);

            const backendMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Delete failed";

            toast.error(backendMessage);

            setDeleteOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteOpen(false);
        setDeleteMessage("");
        setDeleteError(false);
    };

    return (
        <Box className="view-engineer-container">
            <TableContainer component={Paper} className="engineer-table-wrapper" sx={{
                maxHeight: "60vh",        // 🔥 IMPORTANT: set height
                overflowY: "auto",     // 🔥 enables vertical scroll
            }}>
                <Table
                    stickyHeader
                    sx={{
                        "& .MuiTableCell-root": {
                            fontSize: 13,
                            color: "#1e293b",
                            borderBottom: "1px solid #e2e8f0",
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {[
                                "Sr No",
                                "First Name",
                                "Last Name",
                                "Email",
                                "Phone Number",
                                "Assigned Site",
                                // "Supervisor",
                            ].map((text) => (
                                <TableCell
                                    key={text}
                                    sx={{
                                        background:
                                            "linear-gradient(to bottom, #EE7F1A 0%, #f2b14b 55%, #eea53a 100%)",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        fontSize: 13,
                                    }}
                                >
                                    {text}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => (
                                <TableRow key={row.id} hover>
                                    {/* Continuous Serial Number calculation */}
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell>{row.first_name}</TableCell>
                                    <TableCell>{row.last_name}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>{row.phone_no}</TableCell>
                                    <TableCell>
                                        <Box className="site-badge">{row.site_name}</Box>
                                    </TableCell>

                                    {/* <TableCell align="center">
                  <IconButton color="primary" onClick={() => handleEditClick(row)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteClick(row)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell> */}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>

                {/* Add this after </TableContainer> */}
                {!loading && rows.length > 0 && (
                    <div className="pagination-bar">
                        <div className="rows-select">
                            Rows per page:
                            <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                            </select>
                        </div>
                        <div className="page-info">
                            {page * rowsPerPage + 1}-
                            {Math.min((page + 1) * rowsPerPage, rows.length)} of {rows.length}
                        </div>
                        <div className="page-arrows">
                            <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                                ‹
                            </button>
                            <button
                                disabled={(page + 1) * rowsPerPage >= rows.length}
                                onClick={() => setPage(page + 1)}
                            >
                                ›
                            </button>
                        </div>
                    </div>
                )}
            </TableContainer>

            {/* Edit Modal */}
            {selectedEngineer && (
                <EditEngineer
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    engineer={selectedEngineer}
                    onSave={handleSaveEdit}
                />
            )}

            {/* Delete Dialog */}
            <Dialog open={deleteOpen} onClose={handleCancelDelete}>
                <DialogTitle>Delete Engineer</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete{" "}
                        <b>
                            {engineerToDelete?.first_name} {engineerToDelete?.last_name}
                        </b>
                        ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <ToastContainer position="top-right" autoClose={3000} theme="light" />
        </Box>
    );
};

export default ViewSiteEngineer;
